import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MemberDisciplinaryStatusClient, MemberDisciplinaryStatusDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberDisciplinaryStatusClient(API_BASE_URL, axiosInstance);

// Query hook - Get all member disciplinary statuses for dropdowns (cached forever)
export const useMemberDisciplinaryStatuses = () => {
  return useQuery({
    queryKey: ['memberDisciplinaryStatuses'],
    queryFn: async () => {
      const response = await apiClient.searchMemberDisciplinaryStatuses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useMemberDisciplinaryStatusSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchMemberDisciplinaryStatuses({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single member disciplinary status by ID
export const useMemberDisciplinaryStatusById = (id: string) => {
  return useQuery({
    queryKey: ['memberDisciplinaryStatuses', id],
    queryFn: async () => {
      const response = await apiClient.getMemberDisciplinaryStatusById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create member disciplinary status
export const useCreateMemberDisciplinaryStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: MemberDisciplinaryStatusDTO) => {
      await apiClient.createMemberDisciplinaryStatus(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberDisciplinaryStatuses'] });
    },
  });
};

// Mutation hook - Update member disciplinary status
export const useUpdateMemberDisciplinaryStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MemberDisciplinaryStatusDTO }) => {
      await apiClient.updateMemberDisciplinaryStatus(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberDisciplinaryStatuses'] });
    },
  });
};

// Mutation hook - Delete member disciplinary status
export const useDeleteMemberDisciplinaryStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteMemberDisciplinaryStatus(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberDisciplinaryStatuses'] });
    },
  });
};